/**
 * Hub Widget Definitions
 *
 * Defines all widgets for the Hub dashboard using the template system.
 * Each widget specifies its data transformation from the source data.
 */

import {
  createMetricWidget,
  createChartWidget,
  createListWidget,
  WIDGET_CATEGORIES,
  WIDGET_SIZES,
  WIDGET_ICONS,
  DATA_SOURCES,
  WidgetTemplate,
} from '../widgetTemplates';
import { GREEN, RED, AMBER, YELLOW, BLUE, CYAN, PURPLE } from '../../../constants/chartColors';

const {
  FiBarChart2,
  FiDollarSign,
  FiTrendingUp,
  FiFolder,
  FiBell,
  FiUsers,
  FiPercent,
  FiHeart,
  FiActivity,
  FiAlertCircle,
  FiGrid,
  FiAlertTriangle,
  FiCpu,
  FiDatabase,
  FiShield,
  FiLifeBuoy,
  FiAlertOctagon,
  FiClock,
  FiBriefcase,
  FiZap,
  FiTarget,
  FiMessageSquare,
  FiClipboard,
  FiMail,
  FiFileText,
} = WIDGET_ICONS;

/**
 * Safely format a date, returning 'N/A' if the date is invalid
 */
const formatDate = (dateValue: string | number | Date | null | undefined) => {
  if (!dateValue) return 'N/A';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString();
};

/**
 * Active Projects Widget
 * Shows the number of active projects
 */
export const activeProjectsWidget = createMetricWidget({
  id: 'active-projects',
  name: 'Active Projects',
  description: 'Number of active projects',
  icon: FiBarChart2,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: false, // Project count is not date-dependent
  refreshRate: 60000,

  getData: (sourceData) => {
    const active = sourceData?.activeProjects || 0;
    const total = sourceData?.totalProjects || 0;

    return {
      value: active,
      label: 'Active Projects',
      format: 'number',
      color: 'cyan',
      subtitle: total > 0 ? `of ${total} total` : undefined,
      link: '/hub/projects',
    };
  },
});

/**
 * Total Budget Widget
 * Shows the aggregated budget across projects
 */
export const totalBudgetWidget = createMetricWidget({
  id: 'total-budget',
  name: 'Total Budget',
  description: 'Aggregated budget across projects',
  icon: FiDollarSign,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 60000,

  getData: (sourceData) => ({
    value: sourceData?.totalBudget || 0,
    label: 'Total Budget',
    format: 'currency',
    color: 'cyan',
    link: '/hub/projects',
  }),
});

/**
 * Total Spent Widget
 * Shows the aggregated spend across projects
 */
export const totalSpentWidget = createMetricWidget({
  id: 'total-spent',
  name: 'Total Spent',
  description: 'Aggregated spend across projects',
  icon: FiDollarSign,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 60000,

  getData: (sourceData) => ({
    value: sourceData?.totalSpent || 0,
    label: 'Total Spent',
    format: 'currency',
    color: 'cyan',
    link: '/hub/projects',
  }),
});

/**
 * Burn Rate Widget
 * Shows the rate of budget consumption
 */
export const burnRateWidget = createMetricWidget({
  id: 'burn-rate',
  name: 'Burn Rate',
  description: 'Rate of budget consumption',
  icon: FiPercent,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 60000,

  getData: (sourceData) => {
    const burnRate = sourceData?.burnRate || 0;
    const roundedBurnRate = Math.round(burnRate * 100) / 100; // Round to 2 decimals

    return {
      value: roundedBurnRate,
      label: 'Burn Rate',
      format: 'percentage',
      color: 'cyan',
      trend: roundedBurnRate > 80 ? 'up' : 'down',
      trendValue: roundedBurnRate > 80 ? 'High' : 'Normal',
      link: '/hub/projects',
    };
  },
});

/**
 * Monthly Spend Trend Widget
 * Shows a bar chart of spending over time
 */
export const monthlySpendTrendWidget = createChartWidget({
  id: 'monthly-spend-trend',
  name: 'Monthly Spend Trend',
  description: 'Line chart of spending over time',
  icon: FiTrendingUp,
  category: WIDGET_CATEGORIES.HUB,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 300000,

  getData: (sourceData) => {
    const monthlySpend = sourceData?.monthlySpend || [];

    // Transform finance API monthly_breakdown format to chart format
    // API returns: { month: "2024-01", projects: 5, budget: 1000, spend: 600 }
    // Chart needs: { month: "2024-01", value: 600 }
    const data = monthlySpend.map((item: { month?: string; period?: string; spend?: number; amount?: number; value?: number }) => ({
      month: item.month || item.period,
      value: Number.parseFloat(String(item.spend || item.amount || item.value || 0)),
    })).sort((a: { month?: string }, b: { month?: string }) => (a.month || '').localeCompare(b.month || '')); // Sort by month ascending

    return {
      chartType: 'line',
      data: data.length > 0 ? data : [{ month: 'No data', value: 0 }],
      xKey: 'month',
      yKey: 'value',
      color: BLUE,
      strokeWidth: 2,
      link: '/hub/projects',
    };
  },
});

/**
 * Project Status Distribution Widget
 * Shows projects by status as a pie chart
 */
export const projectStatusDistributionWidget = createChartWidget({
  id: 'project-status-distribution',
  name: 'Project Status',
  description: 'Projects by status',
  icon: FiBarChart2,
  category: WIDGET_CATEGORIES.HUB,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: false, // Show all projects regardless of date
  refreshRate: 60000,

  getData: (sourceData) => {
    // Use allProjects to show all projects, not date-filtered
    const projects = sourceData?.allProjects || [];

    // Calculate status distribution
    const statusCounts: Record<string, number> = {};
    projects.forEach((p: { status?: string }) => {
      const status = p.status || 'unknown';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    const data = Object.entries(statusCounts).map(([name, value]) => ({
      name: formatStatusLabel(name),
      value,
    }));

    return {
      chartType: 'pie',
      data: data.length > 0 ? data : [{ name: 'No projects', value: 1 }],
      dataKey: 'value',
      link: '/hub/projects',
    };
  },
});

// Helper to format status labels for display
const formatStatusLabel = (status: string) => {
  const labels: Record<string, string> = {
    'in_progress': 'In Progress',
    'active': 'Active',
    'completed': 'Completed',
    'pending': 'Pending',
    'cancelled': 'Cancelled',
    'on_hold': 'On Hold',
    'planning': 'Planning',
    'unknown': 'Unknown',
  };
  return labels[status?.toLowerCase()] || status;
};

/**
 * Recent Projects List Widget
 * Shows recently updated projects
 */
export const recentProjectsWidget = createListWidget({
  id: 'recent-projects',
  name: 'Recent Projects',
  description: 'Recently updated projects',
  icon: FiFolder,
  category: WIDGET_CATEGORIES.HUB,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: false, // Show all projects sorted by recent activity
  refreshRate: 60000,

  getData: (sourceData) => {
    // Use allProjects to show recent projects regardless of date filter
    const projects = sourceData?.allProjects || [];

    const getProjectStatusVariant = (status: string) => {
      if (['in_progress', 'active'].includes(status)) return 'info';
      if (status === 'completed') return 'success';
      return 'warning';
    };

    const items = projects
      .sort((a: { updatedAt?: string; createdAt?: string }, b: { updatedAt?: string; createdAt?: string }) => new Date(b.updatedAt || b.createdAt || 0).getTime() - new Date(a.updatedAt || a.createdAt || 0).getTime())
      .slice(0, 5)
      .map((p: { name?: string; clientName?: string; client_name?: string; progress?: number; updatedAt?: string; createdAt?: string; id?: string; status?: string }) => ({
        title: p.name,
        subtitle: p.clientName || p.client_name || 'No client',
        value: `${p.progress || 0}%`,
        timestamp: formatDate(p.updatedAt || p.createdAt),
        link: `/hub/projects/${p.id}`,
        badge: {
          text: formatStatusLabel(p.status || '') || 'Unknown',
          variant: getProjectStatusVariant(p.status || ''),
        },
      }));

    return {
      items,
      maxItems: 5,
      showBadge: true,
    };
  },
});

/**
 * Unread Notifications Widget
 * Shows count of unread notifications
 */
export const unreadNotificationsWidget = createMetricWidget({
  id: 'unread-notifications',
  name: 'Unread Notifications',
  description: 'Count of unread notifications',
  icon: FiBell,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: false,
  refreshRate: 30000,

  getData: (sourceData) => {
    const notifications = sourceData?.notifications || [];
    const unread = notifications.filter((n: { read?: boolean }) => !n.read).length;

    return {
      value: unread,
      label: 'Unread Notifications',
      format: 'number',
      color: 'blue',
      link: '/hub',
    };
  },
});

/**
 * Recent Notifications List Widget
 * Shows latest system notifications
 */
export const recentNotificationsWidget = createListWidget({
  id: 'recent-notifications',
  name: 'Recent Notifications',
  description: 'Latest system notifications',
  icon: FiBell,
  category: WIDGET_CATEGORIES.HUB,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: false,
  refreshRate: 60000,

  getData: (sourceData) => {
    const notifications = sourceData?.notifications || [];

    const getNotificationColor = (priority: string) => {
      if (priority === 'high') return 'red';
      if (priority === 'medium') return 'amber';
      return 'blue';
    };

    const items = notifications.slice(0, 5).map((n: { message?: string; title?: string; type?: string; timestamp?: string; createdAt?: string; priority?: string }) => ({
      title: n.message || n.title,
      subtitle: n.type || 'Notification',
      timestamp: formatDate(n.timestamp || n.createdAt),
      badge: {
        text: n.priority || 'Info',
        color: getNotificationColor(n.priority || ''),
      },
    }));

    return {
      items,
      maxItems: 5,
      showBadge: true,
    };
  },
});

/**
 * Hours by Person Widget
 * Shows time tracking per team member
 */
export const hoursByPersonWidget = createChartWidget({
  id: 'hours-by-person',
  name: 'Hours by Person',
  description: 'Time tracking per team member',
  icon: FiUsers,
  category: WIDGET_CATEGORIES.HUB,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 300000,

  getData: (sourceData) => {
    const hoursByPerson = sourceData?.hoursByPerson || [];

    // Use real data if available, take top 10
    const data = hoursByPerson.length > 0
      ? hoursByPerson.slice(0, 10)
      : [];

    return {
      chartType: 'bar',
      data: data.length > 0 ? data : [{ name: 'No data', hours: 0 }],
      xKey: 'name',
      yKey: 'hours',
      color: CYAN,
      link: '/hub/projects?view=timesheet',
    };
  },
});

// =============================================================================
// EXECUTIVE COMMAND CENTER WIDGETS
// These widgets aggregate data from ALL modules for the unified Hub dashboard
// =============================================================================

/**
 * System Health Score Widget
 * Aggregate health score from all platform modules
 */
export const systemHealthScoreWidget = createMetricWidget({
  id: 'system-health-score',
  name: 'System Health',
  description: 'Aggregate health score across all platform modules',
  icon: FiHeart,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: false,
  refreshRate: 60000,

  getData: (sourceData) => {
    const score = sourceData?.systemHealthScore || 0;
    let scoreColor: string;
    let scoreTrend: string;
    let scoreTrendValue: string;
    if (score >= 80) {
      scoreColor = 'green';
      scoreTrend = 'up';
      scoreTrendValue = 'Healthy';
    } else if (score >= 50) {
      scoreColor = 'yellow';
      scoreTrend = 'flat';
      scoreTrendValue = 'Needs Attention';
    } else {
      scoreColor = 'red';
      scoreTrend = 'down';
      scoreTrendValue = 'Critical';
    }
    return {
      value: score,
      label: 'System Health',
      format: 'percentage',
      color: scoreColor,
      trend: scoreTrend,
      trendValue: scoreTrendValue,
      link: '/hub',
    };
  },
});

/**
 * Unified Total Cost Widget
 * Combined costs: LLM + Cloud + Projects
 */
export const unifiedTotalCostWidget = createMetricWidget({
  id: 'unified-total-cost',
  name: 'Total Platform Cost',
  description: 'Combined LLM, Cloud, and Project costs',
  icon: FiDollarSign,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 300000,

  getData: (sourceData) => ({
    value: sourceData?.unifiedTotalCost || 0,
    label: 'Total Platform Cost',
    format: 'currency',
    color: 'blue',
    subtitle: `LLM: $${(sourceData?.llmCost || 0).toFixed(2)} | Cloud: $${(sourceData?.cloudSpend || 0).toFixed(2)}`,
    link: '/hub/projects',
  }),
});

/**
 * Total Active Operations Widget
 * Active agents + running pipelines + deployments
 */
export const totalActiveOperationsWidget = createMetricWidget({
  id: 'total-active-operations',
  name: 'Active Operations',
  description: 'Active agents, pipelines, and deployments',
  icon: FiActivity,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: false,
  refreshRate: 30000,

  getData: (sourceData) => ({
    value: sourceData?.totalActiveOperations || 0,
    label: 'Active Operations',
    format: 'number',
    color: 'purple',
    trend: sourceData?.totalActiveOperations > 0 ? 'up' : 'flat',
    trendValue: sourceData?.totalActiveOperations > 0 ? 'Processing' : 'Idle',
    link: '/hub',
  }),
});

/**
 * Platform Error Rate Widget
 * Aggregated error rate across all modules
 */
export const platformErrorRateWidget = createMetricWidget({
  id: 'platform-error-rate',
  name: 'Platform Error Rate',
  description: 'Aggregated error rate across all modules',
  icon: FiAlertCircle,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 60000,

  getData: (sourceData) => {
    const rate = sourceData?.platformErrorRate || 0;
    let rateColor: string;
    let rateTrend: string;
    let rateTrendValue: string;
    if (rate < 2) {
      rateColor = 'green';
      rateTrend = 'down';
      rateTrendValue = 'Low';
    } else if (rate < 5) {
      rateColor = 'yellow';
      rateTrend = 'flat';
      rateTrendValue = 'Moderate';
    } else {
      rateColor = 'red';
      rateTrend = 'up';
      rateTrendValue = 'High';
    }
    return {
      value: rate,
      label: 'Error Rate',
      format: 'percentage',
      color: rateColor,
      trend: rateTrend,
      trendValue: rateTrendValue,
      link: '/hub',
    };
  },
});

/**
 * Module Status Grid Widget
 * Visual grid showing health of all platform modules
 */
export const moduleStatusGridWidget = {
  id: 'module-status-grid',
  name: 'Module Status',
  type: 'custom',
  description: 'Health status of all platform modules',
  icon: FiGrid,
  category: WIDGET_CATEGORIES.HUB,
  defaultSize: WIDGET_SIZES.WIDE,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: false,
  refreshRate: 60000,

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getData: (sourceData: Record<string, any>) => ({
    modules: [
      {
        name: 'GenAIOps',
        status: sourceData?.moduleHealth?.genaiops || 'unavailable',
        metric: sourceData?.llmQuality ? `${sourceData.llmQuality}% quality` : 'N/A',
        link: '/ai-operations',
      },
      {
        name: 'AgentOps',
        status: sourceData?.moduleHealth?.agentops || 'unavailable',
        metric: sourceData?.agentSuccessRate ? `${sourceData.agentSuccessRate.toFixed(0)}% success` : 'N/A',
        link: '/ai-operations',
      },
      {
        name: 'DataOps',
        status: sourceData?.moduleHealth?.dataops || 'unavailable',
        metric: sourceData?.pipelineSuccessRate ? `${sourceData.pipelineSuccessRate}% success` : 'N/A',
        link: '/dataops',
      },
      {
        name: 'CloudOps',
        status: sourceData?.moduleHealth?.platformops || 'unavailable',
        metric: sourceData?.cloudCompliance ? `${sourceData.cloudCompliance}% compliant` : 'N/A',
        link: '/cloudops',
      },
    ],
  }),
};

/**
 * Cross-Platform Alerts Widget
 * Critical alerts from all modules
 * NOTE: Project Health Heatmap Widget moved to healthWidgets.js
 */
export const crossPlatformAlertsWidget = createListWidget({
  id: 'cross-platform-alerts',
  name: 'Platform Alerts',
  description: 'Critical alerts from all platform modules',
  icon: FiAlertTriangle,
  category: WIDGET_CATEGORIES.HUB,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: false,
  refreshRate: 30000,

  getData: (sourceData) => {
    const alerts = sourceData?.crossModuleAlerts || [];

    // Navigation links for each module
    const moduleLinks: Record<string, string> = {
      'GenAIOps': '/ai-operations',
      'AgentOps': '/ai-operations',
      'DataOps': '/dataops',
      'CloudOps': '/cloudops',
    };

    const getAlertColor = (severity: string) => {
      if (severity === 'critical') return 'red';
      if (severity === 'warning') return 'yellow';
      return 'blue';
    };

    const items = alerts.map((alert: { severity?: string; module?: string; message?: string }) => ({
      title: alert.module,
      subtitle: alert.message,
      link: moduleLinks[alert.module || ''],
      badge: {
        text: alert.severity,
        color: getAlertColor(alert.severity || ''),
      },
    }));

    // If no alerts, show a positive message
    if (items.length === 0) {
      items.push({
        title: 'All Systems Operational',
        subtitle: 'No critical alerts at this time',
        badge: { text: 'Healthy', color: 'green' },
      });
    }

    return {
      items,
      maxItems: 5,
      showBadge: true,
    };
  },
});

/**
 * LLM Cost Trend Widget
 * Chart showing LLM spending over time
 */
export const llmCostTrendWidget = createChartWidget({
  id: 'llm-cost-trend',
  name: 'LLM Cost Trend',
  description: 'LLM spending over time',
  icon: FiTrendingUp,
  category: WIDGET_CATEGORIES.HUB,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 300000,

  getData: (sourceData) => {
    const rawTimeseries = sourceData?.genaiops?.tracesTimeseries;
    const timeseries = Array.isArray(rawTimeseries) ? rawTimeseries : (rawTimeseries?.data || []);

    const data = timeseries.map((item: { hour?: string; date?: string; total_cost?: number; count?: number }) => ({
      date: item.hour || item.date,
      cost: item.total_cost || 0,
      traces: item.count || 0,
    }));

    return {
      chartType: 'line',
      data: data.length > 0 ? data : [{ date: 'No data', cost: 0 }],
      xKey: 'date',
      yKey: 'cost',
      color: PURPLE,
      link: '/genaiops/observability',
    };
  },
});

/**
 * Agent Activity Summary Widget
 * Total agent runs today
 */
export const agentActivitySummaryWidget = createMetricWidget({
  id: 'agent-activity-summary',
  name: 'Agent Runs',
  description: 'Total agent runs in period',
  icon: FiCpu,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 60000,

  getData: (sourceData) => ({
    value: sourceData?.agentRuns || 0,
    label: 'Agent Runs',
    format: 'number',
    color: 'purple',
    subtitle: `${sourceData?.agentActiveRuns || 0} active`,
    link: '/agentops/runs',
  }),
});

/**
 * Pipeline Health Widget
 * Data pipeline success rate
 */
export const pipelineHealthWidget = createMetricWidget({
  id: 'pipeline-health',
  name: 'Pipeline Health',
  description: 'Data pipeline success rate',
  icon: FiDatabase,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 60000,

  getData: (sourceData) => {
    const rate = sourceData?.pipelineSuccessRate || 0;
    let pipelineColor: string;
    if (rate >= 90) {
      pipelineColor = 'green';
    } else if (rate >= 70) {
      pipelineColor = 'yellow';
    } else {
      pipelineColor = 'red';
    }
    return {
      value: rate,
      label: 'Pipeline Success',
      format: 'percentage',
      color: pipelineColor,
      link: '/dataops/catalog',
    };
  },
});

/**
 * Cloud Compliance Summary Widget
 * Infrastructure compliance score
 */
export const cloudComplianceSummaryWidget = createMetricWidget({
  id: 'cloud-compliance-summary',
  name: 'Cloud Compliance',
  description: 'Infrastructure compliance score',
  icon: FiShield,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: false,
  refreshRate: 300000,

  getData: (sourceData) => {
    const score = sourceData?.cloudCompliance || 0;
    let complianceColor: string;
    if (score >= 90) {
      complianceColor = 'green';
    } else if (score >= 70) {
      complianceColor = 'yellow';
    } else {
      complianceColor = 'red';
    }
    return {
      value: score,
      label: 'Compliance',
      format: 'percentage',
      color: complianceColor,
      link: '/cloudops/guardrails',
    };
  },
});

// ============================================
// Additional Hub Widgets (Phase 10)
// ============================================

/**
 * Pending Support Widget
 * Shows count of open support tickets
 */
export const pendingSupportWidget = createMetricWidget({
  id: 'pending-support',
  name: 'Pending Support Requests',
  description: 'Count of open support tickets',
  icon: FiLifeBuoy,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: false,
  refreshRate: 30000,
  getData: (sourceData) => ({
    value: sourceData?.pendingSupport || sourceData?.open_tickets || 0,
    label: 'Pending Support',
    format: 'number',
    color: 'amber',
    link: '/hub/support',
  }),
});

/**
 * Critical Alerts Widget
 * Shows high-priority alerts requiring attention
 */
export const criticalAlertsWidget = createMetricWidget({
  id: 'critical-alerts',
  name: 'Critical Alerts',
  description: 'High-priority alerts requiring attention',
  icon: FiAlertOctagon,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: false,
  refreshRate: 30000,
  getData: (sourceData) => ({
    value: sourceData?.criticalAlerts || sourceData?.critical_count || 0,
    label: 'Critical Alerts',
    format: 'number',
    color: 'red',
    link: '/hub',
  }),
});

/**
 * AI Model Costs Widget
 * Shows cost distribution by AI model
 */
export const aiModelCostsWidget = createChartWidget({
  id: 'ai-model-costs',
  name: 'AI Model Costs',
  description: 'Cost distribution by AI model',
  icon: FiCpu,
  category: WIDGET_CATEGORIES.HUB,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const modelCosts = sourceData?.aiModelCosts || sourceData?.model_costs || [];
    const data = modelCosts.length > 0 ? modelCosts : [
      { model: 'GPT-4', cost: 450 },
      { model: 'Claude', cost: 320 },
      { model: 'GPT-3.5', cost: 85 },
    ];
    return {
      chartType: 'pie',
      data,
      dataKey: 'cost',
      nameKey: 'model',
      colors: [PURPLE, BLUE, GREEN],
      link: '/genaiops/observability',
    };
  },
});

/**
 * Project Progress Widget
 * Shows overall project completion
 */
export const projectProgressWidget = createMetricWidget({
  id: 'project-progress',
  name: 'Project Progress',
  description: 'Overall project completion',
  icon: FiTrendingUp,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => {
    const projects = sourceData?.allProjects || [];
    const avgProgress = projects.length > 0
      ? projects.reduce((sum: number, p: { progress?: number }) => sum + (p.progress || 0), 0) / projects.length
      : sourceData?.projectProgress || 0;
    return {
      value: Math.round(avgProgress),
      label: 'Project Progress',
      format: 'percentage',
      color: 'cyan',
      link: '/hub/projects',
    };
  },
});

/**
 * Remaining Budget Widget
 * Shows available budget amount
 */
export const remainingBudgetWidget = createMetricWidget({
  id: 'remaining-budget',
  name: 'Remaining Budget',
  description: 'Available budget amount',
  icon: FiDollarSign,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => {
    const total = sourceData?.totalBudget || 0;
    const spent = sourceData?.totalSpent || 0;
    const remaining = sourceData?.remainingBudget || (total - spent);
    return {
      value: remaining,
      label: 'Remaining Budget',
      format: 'currency',
      color: remaining > 0 ? 'green' : 'red',
      link: '/hub/projects',
    };
  },
});

/**
 * Total Hours Logged Widget
 * Shows aggregated timesheet hours
 */
export const totalHoursLoggedWidget = createMetricWidget({
  id: 'total-hours-logged',
  name: 'Total Hours Logged',
  description: 'Aggregated timesheet hours',
  icon: FiClock,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.totalHoursLogged || sourceData?.total_hours || 0,
    label: 'Hours Logged',
    format: 'number',
    color: 'blue',
    link: '/hub/projects?view=timesheet',
  }),
});

/**
 * Billable Hours Widget
 * Shows total billable time
 */
export const billableHoursWidget = createMetricWidget({
  id: 'billable-hours',
  name: 'Billable Hours',
  description: 'Total billable time',
  icon: FiBriefcase,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.billableHours || sourceData?.billable_time || 0,
    label: 'Billable Hours',
    format: 'number',
    color: 'green',
    link: '/hub/projects?view=timesheet',
  }),
});

/**
 * Team Utilization Widget
 * Shows resource utilization percentage
 */
export const teamUtilizationWidget = createMetricWidget({
  id: 'team-utilization',
  name: 'Team Utilization',
  description: 'Resource utilization percentage',
  icon: FiUsers,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.teamUtilization || sourceData?.utilization_rate || 0,
    label: 'Utilization',
    format: 'percentage',
    color: 'cyan',
    link: '/hub/projects?view=timesheet',
  }),
});

/**
 * Hours by Project Widget
 * Shows time distribution across projects
 */
export const hoursByProjectWidget = createChartWidget({
  id: 'hours-by-project',
  name: 'Hours by Project',
  description: 'Time distribution across projects',
  icon: FiBarChart2,
  category: WIDGET_CATEGORIES.HUB,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const hoursByProject = sourceData?.hoursByProject || sourceData?.project_hours || [];
    const data = hoursByProject.length > 0 ? hoursByProject.slice(0, 10) : [];
    return {
      chartType: 'horizontalBar',
      data: data.length > 0 ? data : [{ project: 'No data', hours: 0 }],
      xKey: 'project',
      yKey: 'hours',
      color: CYAN,
      link: '/hub/projects?view=timesheet',
    };
  },
});

/**
 * Support Response Time Widget
 * Shows average support response time
 */
export const supportResponseTimeWidget = createMetricWidget({
  id: 'support-response-time',
  name: 'Avg Response Time',
  description: 'Average support response time',
  icon: FiZap,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const responseTime = sourceData?.avg_response_time_minutes
      || sourceData?.supportResponseTime
      || sourceData?.avg_response_mins
      || 0;
    return {
      value: responseTime,
      label: 'Response Time',
      format: 'duration',
      color: 'blue',
      displayValue: `${responseTime} min`,
      link: '/hub/support',
    };
  },
});

/**
 * Support by Priority Widget
 * Shows tickets by priority level
 */
export const supportByPriorityWidget = createChartWidget({
  id: 'support-by-priority',
  name: 'Support by Priority',
  description: 'Tickets by priority level',
  icon: FiTarget,
  category: WIDGET_CATEGORIES.HUB,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => {
    const priorityBreakdown = sourceData?.supportByPriority || sourceData?.priority_breakdown || [];
    const data = priorityBreakdown.length > 0 ? priorityBreakdown : [
      { priority: 'Critical', count: 2 },
      { priority: 'High', count: 8 },
      { priority: 'Medium', count: 15 },
      { priority: 'Low', count: 12 },
    ];
    return {
      chartType: 'donut',
      data,
      dataKey: 'count',
      nameKey: 'priority',
      colors: [RED, AMBER, YELLOW, GREEN],
      link: '/hub/support',
    };
  },
});

/**
 * Project Health Score Widget
 * Shows overall project health indicator
 */
export const projectHealthScoreWidget = createMetricWidget({
  id: 'project-health-score',
  name: 'Project Health',
  description: 'Overall project health indicator',
  icon: FiHeart,
  category: WIDGET_CATEGORIES.HUB,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => {
    const score = sourceData?.projectHealthScore || sourceData?.health_score || 0;
    let projectHealthColor: string;
    if (score >= 80) {
      projectHealthColor = 'green';
    } else if (score >= 50) {
      projectHealthColor = 'yellow';
    } else {
      projectHealthColor = 'red';
    }
    return {
      value: score,
      label: 'Project Health',
      format: 'percentage',
      color: projectHealthColor,
      link: '/hub/projects',
    };
  },
});

/**
 * Milestone Progress Widget
 * Shows milestone completion status
 */
export const milestoneProgressWidget = createChartWidget({
  id: 'milestone-progress',
  name: 'Milestone Progress',
  description: 'Milestone completion status',
  icon: FiTarget,
  category: WIDGET_CATEGORIES.HUB,
  defaultSize: WIDGET_SIZES.LARGE,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => {
    const milestones = sourceData?.milestoneProgress || sourceData?.milestones || [];
    const data = milestones.length > 0 ? milestones : [
      { milestone: 'Phase 1', progress: 100 },
      { milestone: 'Phase 2', progress: 75 },
      { milestone: 'Phase 3', progress: 30 },
      { milestone: 'Phase 4', progress: 0 },
    ];
    return {
      chartType: 'bar',
      data,
      xKey: 'milestone',
      yKey: 'progress',
      color: GREEN,
      link: '/hub/projects',
    };
  },
});

/**
 * Assessment Score Widget
 * Shows AI strategy assessment score
 */
export const assessmentScoreWidget = createMetricWidget({
  id: 'assessment-score',
  name: 'Strategy Score',
  description: 'AI strategy assessment score',
  icon: FiTarget,
  category: WIDGET_CATEGORIES.HUB,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: false,
  refreshRate: 600000,
  getData: (sourceData) => ({
    value: sourceData?.assessmentScore || sourceData?.readiness_score || 0,
    label: 'Readiness',
    format: 'percentage',
    color: 'purple',
    link: '/hub/assessments',
  }),
});

/**
 * Category Scores Widget
 * Shows scores by category
 */
export const categoryScoresWidget = createChartWidget({
  id: 'category-scores',
  name: 'Category Scores',
  description: 'Scores by category',
  icon: FiBarChart2,
  category: WIDGET_CATEGORIES.HUB,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: false,
  refreshRate: 600000,
  getData: (sourceData) => {
    const scores = sourceData?.categoryScores || sourceData?.category_breakdown || [];
    const data = scores.length > 0 ? scores : [
      { category: 'Data', score: 78 },
      { category: 'Technology', score: 82 },
      { category: 'People', score: 65 },
      { category: 'Process', score: 71 },
    ];
    return {
      chartType: 'bar',
      data,
      xKey: 'category',
      yKey: 'score',
      color: PURPLE,
      link: '/hub/assessments',
    };
  },
});

/**
 * Projects by Client Widget
 * Shows project count per client
 */
export const projectsByClientWidget = createChartWidget({
  id: 'projects-by-client',
  name: 'Projects by Client',
  description: 'Project count per client',
  icon: FiUsers,
  category: WIDGET_CATEGORIES.HUB,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => {
    const projects = sourceData?.allProjects || [];
    const clientCounts: Record<string, number> = {};
    projects.forEach((p: { clientName?: string; client_name?: string }) => {
      const client = p.clientName || p.client_name || 'Unknown';
      clientCounts[client] = (clientCounts[client] || 0) + 1;
    });
    const data = Object.entries(clientCounts).map(([client, count]) => ({
      client,
      count,
    }));
    return {
      chartType: 'horizontalBar',
      data: data.length > 0 ? data.slice(0, 10) : [{ client: 'No data', count: 0 }],
      xKey: 'client',
      yKey: 'count',
      color: CYAN,
      link: '/hub/projects',
    };
  },
});

/**
 * Model Cost Distribution Widget
 * Shows cost distribution by model
 */
export const modelCostDistributionWidget = createChartWidget({
  id: 'model-cost-distribution',
  name: 'Model Cost Distribution',
  description: 'Cost distribution by model',
  icon: FiCpu,
  category: WIDGET_CATEGORIES.HUB,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const distribution = sourceData?.modelCostDistribution || sourceData?.genaiops?.costByModel || [];
    const data = distribution.length > 0 ? distribution : [
      { model: 'claude-3-opus', cost: 280 },
      { model: 'claude-3-sonnet', cost: 420 },
      { model: 'gpt-4', cost: 350 },
    ];
    return {
      chartType: 'pie',
      data,
      dataKey: 'cost',
      nameKey: 'model',
      colors: [PURPLE, BLUE, GREEN, AMBER],
      link: '/genaiops/observability',
    };
  },
});

/**
 * Non-Billable Hours Widget
 * Shows total non-billable time
 */
export const nonBillableHoursWidget = createMetricWidget({
  id: 'non-billable-hours',
  name: 'Non-Billable Hours',
  description: 'Total non-billable time',
  icon: FiClock,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.nonBillableHours || sourceData?.non_billable_time || 0,
    label: 'Non-Billable',
    format: 'number',
    color: 'gray',
    link: '/hub/projects?view=timesheet',
  }),
});

/**
 * Recent Support Activity Widget
 * Shows latest support updates
 */
export const recentSupportActivityWidget = createListWidget({
  id: 'recent-support-activity',
  name: 'Support Activity',
  description: 'Latest support updates',
  icon: FiMessageSquare,
  category: WIDGET_CATEGORIES.HUB,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => {
    const activity = sourceData?.supportActivity || sourceData?.recent_tickets || [];
    const getTicketPriorityColor = (priority: string) => {
      if (priority === 'Critical') return 'red';
      if (priority === 'High') return 'amber';
      return 'blue';
    };
    const items = activity.slice(0, 5).map((item: { subject?: string; title?: string; status?: string; createdAt?: string; created_at?: string; priority?: string }) => ({
      title: item.subject || item.title,
      subtitle: item.status || 'Open',
      timestamp: formatDate(item.createdAt || item.created_at),
      badge: {
        text: item.priority || 'Medium',
        color: getTicketPriorityColor(item.priority || ''),
      },
    }));
    return {
      items,
      maxItems: 5,
      showBadge: true,
    };
  },
});

/**
 * Activity by Type Widget
 * Shows actions distribution
 */
export const activityByTypeWidget = createChartWidget({
  id: 'activity-by-type',
  name: 'Activity by Type',
  description: 'Actions distribution',
  icon: FiBarChart2,
  category: WIDGET_CATEGORIES.HUB,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const activityTypes = sourceData?.activityByType || sourceData?.activity_distribution || [];
    const data = activityTypes.length > 0 ? activityTypes : [
      { type: 'Create', count: 45 },
      { type: 'Update', count: 120 },
      { type: 'Delete', count: 15 },
      { type: 'View', count: 280 },
    ];
    return {
      chartType: 'bar',
      data,
      xKey: 'type',
      yKey: 'count',
      color: CYAN,
      link: '/hub',
    };
  },
});

/**
 * User Activity Timeline Widget
 * Shows chronological user actions
 */
export const userActivityTimelineWidget = createListWidget({
  id: 'user-activity-timeline',
  name: 'Activity Timeline',
  description: 'Chronological user actions',
  icon: FiClock,
  category: WIDGET_CATEGORIES.HUB,
  defaultSize: WIDGET_SIZES.LARGE,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => {
    const timeline = sourceData?.activityTimeline || sourceData?.recent_activity || [];
    const items = timeline.slice(0, 10).map((item: { action?: string; type?: string; user?: string; description?: string; timestamp?: string; created_at?: string }) => ({
      title: item.action || item.type,
      subtitle: item.user || 'System',
      description: item.description,
      timestamp: formatDate(item.timestamp || item.created_at),
    }));
    return {
      items,
      maxItems: 10,
      showBadge: false,
    };
  },
});

/**
 * Assessment Completion Rate Widget
 * Shows completed assessments percentage
 */
export const assessmentCompletionRateWidget = createMetricWidget({
  id: 'assessment-completion-rate',
  name: 'Assessment Completion',
  description: 'Completed assessments %',
  icon: FiClipboard,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: false,
  refreshRate: 600000,
  getData: (sourceData) => ({
    value: sourceData?.assessmentCompletionRate || sourceData?.completion_rate || 0,
    label: 'Completion',
    format: 'percentage',
    color: 'green',
    link: '/hub/assessments',
  }),
});

/**
 * Recommendations Count Widget
 * Shows generated recommendations count
 */
export const recommendationsCountWidget = createMetricWidget({
  id: 'recommendations-count',
  name: 'Active Recommendations',
  description: 'Generated recommendations',
  icon: FiZap,
  category: WIDGET_CATEGORIES.HUB,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.recommendationsCount || sourceData?.active_recommendations || 0,
    label: 'Recommendations',
    format: 'number',
    color: 'purple',
    link: '/hub/assessments',
  }),
});

/**
 * Client Communication History Widget
 * Shows message timeline
 */
export const clientCommunicationHistoryWidget = createListWidget({
  id: 'client-communication-history',
  name: 'Communication History',
  description: 'Message timeline',
  icon: FiMail,
  category: WIDGET_CATEGORIES.HUB,
  defaultSize: WIDGET_SIZES.LARGE,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 120000,
  getData: (sourceData) => {
    const communications = sourceData?.communicationHistory || sourceData?.messages || [];
    const items = communications.slice(0, 10).map((item: { subject?: string; title?: string; sender?: string; from?: string; sentAt?: string; timestamp?: string; type?: string }) => ({
      title: item.subject || item.title,
      subtitle: item.sender || item.from,
      timestamp: formatDate(item.sentAt || item.timestamp),
      badge: {
        text: item.type || 'Email',
        color: 'blue',
      },
    }));
    return {
      items,
      maxItems: 10,
      showBadge: true,
    };
  },
});

/**
 * Recent Audit Log Widget
 * Shows recent system activity
 */
export const recentAuditLogWidget = createListWidget({
  id: 'recent-audit-log',
  name: 'Recent Audit Log',
  description: 'Recent system activity',
  icon: FiFileText,
  category: WIDGET_CATEGORIES.HUB,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.HUB,
  supportsDateFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => {
    const auditLog = sourceData?.auditLog || sourceData?.recent_audit || [];
    const items = auditLog.slice(0, 10).map((item: { action?: string; user?: string; resource?: string; details?: string; timestamp?: string; created_at?: string }) => ({
      title: item.action,
      subtitle: item.user || 'System',
      description: item.resource || item.details,
      timestamp: formatDate(item.timestamp || item.created_at),
    }));
    return {
      items,
      maxItems: 10,
      showBadge: false,
    };
  },
});

/**
 * Export all Hub widgets as an array
 */
export const HUB_WIDGETS = [
  // Executive Command Center widgets (new)
  systemHealthScoreWidget,
  unifiedTotalCostWidget,
  totalActiveOperationsWidget,
  platformErrorRateWidget,
  moduleStatusGridWidget,
  crossPlatformAlertsWidget,
  llmCostTrendWidget,
  agentActivitySummaryWidget,
  pipelineHealthWidget,
  cloudComplianceSummaryWidget,
  // Original Hub widgets
  activeProjectsWidget,
  totalBudgetWidget,
  totalSpentWidget,
  burnRateWidget,
  monthlySpendTrendWidget,
  projectStatusDistributionWidget,
  recentProjectsWidget,
  unreadNotificationsWidget,
  recentNotificationsWidget,
  hoursByPersonWidget,
  // Additional widgets (Phase 10)
  pendingSupportWidget,
  criticalAlertsWidget,
  aiModelCostsWidget,
  projectProgressWidget,
  remainingBudgetWidget,
  totalHoursLoggedWidget,
  billableHoursWidget,
  teamUtilizationWidget,
  hoursByProjectWidget,
  supportResponseTimeWidget,
  supportByPriorityWidget,
  projectHealthScoreWidget,
  milestoneProgressWidget,
  assessmentScoreWidget,
  categoryScoresWidget,
  projectsByClientWidget,
  modelCostDistributionWidget,
  nonBillableHoursWidget,
  recentSupportActivityWidget,
  activityByTypeWidget,
  userActivityTimelineWidget,
  assessmentCompletionRateWidget,
  recommendationsCountWidget,
  clientCommunicationHistoryWidget,
  recentAuditLogWidget,
];

/**
 * Export widgets as a map by ID for quick lookup
 */
export const HUB_WIDGETS_MAP = HUB_WIDGETS.reduce((acc: Record<string, WidgetTemplate>, widget) => {
  acc[widget.id] = widget;
  return acc;
}, {} as Record<string, WidgetTemplate>);

export default HUB_WIDGETS;
